// Criadouro: crie um Pokémon de uma linha bloqueada para liberá-la na sua loja.
// Abaixo, a Pokédex com os 151 de Kanto (liberados x bloqueados).
import { api, errorMessage, type CrecheResponse } from "../api";
import { speciesFor, speciesName, traitName, unitDef, unitName } from "../data";
import { num, t, type MsgKey } from "../i18n";
import type { View } from "../router";
import { loadSession } from "../session";
import { loadSprites, spriteHtml } from "../sprites";
import { esc, screen } from "../ui";
import { PDX } from "./market";

const stageOf = (level: number): 1 | 2 | 3 => (level >= 5 ? 3 : level >= 3 ? 2 : 1);

export const crecheView: View = (root) => {
  root.innerHTML = screen(t("creche.title"), "/menu", `<div class="bw-textbox">${t("common.loading")}</div>`);
  const body = root.querySelector<HTMLElement>(".screen__body")!;
  let data: CrecheResponse | null = null;
  let clockSkew = 0;
  let tick = 0;

  const show = (msg: string, kind = "error") => {
    const a = body.querySelector<HTMLElement>("[data-alert]");
    if (!a) return;
    a.className = `bw-alert bw-alert--${kind}`;
    a.textContent = msg;
    a.hidden = false;
  };

  async function load() {
    try {
      data = await api.creche();
      clockSkew = data.now - Date.now();
      await loadSprites();
      render();
    } catch (e) {
      body.innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(e))}</div>`;
    }
  }

  const bar = (label: string, value: number, cls: string) =>
    `<div class="pet-bar ${cls}"><span>${label}</span><div class="pet-bar__track"><div class="pet-bar__fill" style="width:${Math.max(0, Math.min(100, value))}%"></div></div><b>${Math.round(value)}</b></div>`;

  const left = (last: number, cd: number) => Math.max(0, last ? last + cd - (Date.now() + clockSkew) : 0);
  const fmt = (ms: number) => {
    const m = Math.ceil(ms / 60000);
    return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}` : `${m} min`;
  };

  function petHtml(d: CrecheResponse): string {
    const p = d.pet!;
    const def = unitDef(p.unitId)!;
    const species = speciesFor(p.unitId, stageOf(p.level));
    const happy = p.hunger < 70 && p.happiness >= 40;
    const playLeft = left(p.lastPlay, d.rules.playCooldownMs);
    const trainLeft = left(p.lastTrain, d.rules.trainCooldownMs);
    const mood = p.hunger >= 70 ? "hungry" : p.happiness < 40 ? "sad" : p.happiness >= 75 ? "joy" : "ok";
    return `
      <section class="bw-panel pet-card">
        <div class="pet-stage ${happy ? "happy" : ""}">${spriteHtml(species, 112)}</div>
        <div class="pet-info">
          <p class="pet-name"><b>${esc(speciesName(species) ?? p.unitId)}</b> · ${t("creche.level", { n: p.level, max: d.rules.maxLevel })}</p>
          <p class="bw-dim small">${def.traits.map((k) => `<span class="bw-type" data-type="${k}">${esc(traitName(k))}</span>`).join(" ")} · ${t("creche.unlocks", { name: unitName(p.unitId, 1) })}</p>
          ${bar(t("creche.xp"), (p.xp / d.rules.xpPerLevel) * 100, "xp")}
          ${bar(t("creche.belly"), 100 - p.hunger, "belly")}
          ${bar(t("creche.happiness"), p.happiness, "joy")}
          <p class="pet-mood">${t(`creche.mood.${mood}` as MsgKey)}</p>
          <div class="pet-actions">
            <button class="bw-btn bw-btn--green" data-act="feed" ${p.hunger < 10 ? 'aria-disabled="true"' : ""}>🍖 ${t("creche.feed")} · ${PDX} ${d.rules.feedPrice}</button>
            <button class="bw-btn bw-btn--blue" data-act="play" ${playLeft ? 'aria-disabled="true"' : ""}>🎾 ${playLeft ? fmt(playLeft) : t("creche.play")}</button>
            <button class="bw-btn bw-btn--red" data-act="train" ${trainLeft ? 'aria-disabled="true"' : ""}>💪 ${trainLeft ? fmt(trainLeft) : t("creche.train")}</button>
          </div>
          <p class="bw-dim small">${t("creche.tips")}</p>
          <p><button class="bw-btn small" data-release>${t("creche.release")}</button></p>
        </div>
      </section>`;
  }

  function eggsHtml(d: CrecheResponse): string {
    const options = d.lockedLines.filter((id) => !d.unlocked.includes(id)).sort((a, b) => (unitDef(a)!.cost - unitDef(b)!.cost) || a.localeCompare(b));
    if (!options.length) return `<div class="bw-alert bw-alert--ok">${t("creche.allDone")}</div>`;
    return `
      <div class="bw-textbox">${t("creche.choose")}</div>
      <div class="egg-grid">${options.map((id) => {
        const def = unitDef(id)!;
        return `<button class="bw-panel egg" data-adopt="${id}">
          <span class="egg-cost bw-coin">${def.cost}</span>
          ${spriteHtml(speciesFor(id, 1), 64)}
          <b>${esc(unitName(id, 1))}</b>
          <span class="small">${def.traits.map((k) => esc(traitName(k))).join(" · ")}</span>
        </button>`;
      }).join("")}</div>`;
  }

  /** Pokédex: as 151 espécies, marcando as das linhas bloqueadas que ainda faltam. */
  function dexHtml(d: CrecheResponse): string {
    const lockedSpecies = new Set<number>();
    for (const id of d.lockedLines) {
      if (d.unlocked.includes(id)) continue;
      for (const st of unitDef(id)!.stages) if (typeof st === "number") lockedSpecies.add(st);
    }
    const all = Array.from({ length: 151 }, (_, i) => i + 1);
    const free = all.filter((s) => !lockedSpecies.has(s)).length;
    return `
      <section class="bw-panel bw-stack">
        <div class="bw-panel__header">${t("creche.dex", { n: free, total: 151 })}</div>
        <div class="dex-grid">${all.map((s) => `<span class="dex ${lockedSpecies.has(s) ? "locked" : ""}" title="#${s} ${esc(speciesName(s) ?? "")}">${spriteHtml(s, 40)}<small>${s}</small></span>`).join("")}</div>
      </section>`;
  }

  function render() {
    const d = data;
    if (!d) return;
    body.innerHTML = `
      <div class="pdx-bar bw-panel"><span class="pdx">${PDX} <b>${num(d.pokedollars)}</b></span><span class="bw-dim small">${t("creche.explain", { max: d.rules.maxLevel })}</span></div>
      <div data-alert hidden></div>
      ${d.pet ? petHtml(d) : eggsHtml(d)}
      ${dexHtml(d)}`;
  }

  body.addEventListener("click", async (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-act],[data-adopt],[data-release]");
    if (!el || !data || el.getAttribute("aria-busy") === "true") return;
    el.setAttribute("aria-busy", "true");
    try {
      if (el.dataset.adopt) {
        await api.adopt(el.dataset.adopt);
        await load();
        show(t("creche.adopted", { name: unitName(el.dataset.adopt, 1) }), "ok");
      } else if (el.hasAttribute("data-release")) {
        if (!el.dataset.confirm) {
          el.dataset.confirm = "1";
          el.textContent = t("creche.releaseConfirm");
          return;
        }
        await api.release();
        await load();
      } else if (el.dataset.act) {
        const r = await api.petAction(el.dataset.act as "feed" | "play" | "train");
        await load();
        if (r.unlocked) show(t("creche.unlocked", { name: unitName(r.unlocked, 1) }), "ok");
        else show(t("creche.gotXp", { n: r.xp }), "ok");
        if (el.dataset.act === "feed") void loadSession();
      }
    } catch (err) {
      const code = (err as { code?: string }).code ?? "";
      const known = ["not-hungry", "too-hungry", "too-sad", "cooldown"].includes(code);
      show(known ? t(`creche.err.${code}` as MsgKey) : errorMessage(err));
    } finally {
      el.removeAttribute("aria-busy");
    }
  });

  // atualiza os contadores de espera
  tick = window.setInterval(() => {
    if (data?.pet && !body.querySelector("[aria-busy]")) render();
  }, 30_000);
  void load();
  return () => clearInterval(tick);
};
